import { type QuatLike, RAD2DEG, wrap180, wrap360 } from './math';

export interface HudRotationOptions {
  /** Switch only this far past the 45 deg boundary (deg). */
  hysteresisDeg: number;
  /**
   * Ignore the pose when the screen normal is this close to vertical (deg of
   * camera pitch): the in-screen gravity direction is ill-defined there.
   */
  maxPitchDeg: number;
}

export const DEFAULT_HUD_OPTIONS: HudRotationOptions = {
  hysteresisDeg: 20,
  maxPitchDeg: 60,
};

/**
 * Physical device roll relative to the (locked) screen orientation of an XR
 * viewer, quantised to 0/90/180/270 with hysteresis.
 *
 * The returned angle is the CSS rotation (clockwise, deg) the HUD root must get
 * so that it is upright for the user: 90 when the device was turned 90 deg
 * counter-clockwise (screen top to the left), 270 when turned clockwise.
 */
export class HudRotationTracker {
  readonly opts: HudRotationOptions;
  deg = 0;
  /** Unquantised roll of the last accepted pose (deg), NaN if none. */
  rawDeg = NaN;
  private readonly minInPlane: number;

  constructor(opts: Partial<HudRotationOptions> = {}) {
    this.opts = { ...DEFAULT_HUD_OPTIONS, ...opts };
    this.minInPlane = Math.cos(this.opts.maxPitchDeg / RAD2DEG);
  }

  reset(): void {
    this.deg = 0;
    this.rawDeg = NaN;
  }

  /** Update from a viewer orientation (viewer -> Y-up reference space). */
  update(q: QuatLike | null): number {
    if (!q) return this.deg;
    const x = q[0], y = q[1], z = q[2], w = q[3];
    // World up (0, 1, 0) expressed in viewer coordinates = 2nd row of R.
    const gx = 2 * (x * y + w * z);
    const gy = 1 - 2 * (x * x + z * z);
    if (Math.sqrt(gx * gx + gy * gy) < this.minInPlane) return this.deg;
    const a = wrap360(Math.atan2(gx, gy) * RAD2DEG);
    this.rawDeg = a;
    if (Math.abs(wrap180(a - this.deg)) > 45 + this.opts.hysteresisDeg) {
      this.deg = wrap360(Math.round(a / 90) * 90);
    }
    return this.deg;
  }
}
